<template>
  <div class="form-path-board mb-3" dir="rtl">
    <div class="board-head mb-2">
      <h6 class="mb-1">مسیر شرط‌های فرم</h6>
      <p class="mb-0 text-muted small">روی یک مرحله بزن، بعد بگو «اگر این جواب بود، برو آنجا».</p>
    </div>

    <div v-if="!steps.length" class="empty-board">
      یک «مرحله» از نوار بالا اضافه کن تا بتوانی مسیر بسازی.
    </div>

    <div v-else class="board-steps">
      <div
        v-for="(step, stepIndex) in steps"
        :key="step.id"
        class="step-card"
        :class="{ active: isActive(step) }"
        @click="selectStep(step)"
      >
        <div class="step-card-head">
          <div>
            <span class="step-index">{{ stepIndex + 1 }}</span>
            <strong>{{ step.title || 'بدون عنوان' }}</strong>
          </div>
          <span class="step-hint" v-if="ruleCount(step)">{{ ruleCount(step) }} مسیر شرطی</span>
        </div>

        <div v-if="isActive(step)" class="step-card-body" @click.stop>
          <div
            v-for="(rule, ruleIndex) in rulesOf(step)"
            :key="rule.id || ruleIndex"
            class="mb-2"
          >
            <button
              v-if="editingKey !== ruleKey(step, ruleIndex)"
              type="button"
              class="path-chip"
              @click="editingKey = ruleKey(step, ruleIndex)"
            >
              <span>{{ describeRule(rule) }}</span>
              <span class="chip-arrow">←</span>
              <strong>{{ stepLabel(rule.next) || 'مرحله را انتخاب کن' }}</strong>
            </button>
            <form-path-rule-editor
              v-else
              :rule="rule"
              :step-questions="questionsInStep(step)"
              :other-questions="questionsOutsideStep(step)"
              :other-steps="otherSteps(step)"
              :all-questions="visibleQuestions"
              @remove="removeRule(step, ruleIndex)"
              @done="editingKey = null"
            />
          </div>

          <div class="fallback-row">
            <span>اگر هیچ‌کدام نبود</span>
            <span class="chip-arrow">←</span>
            <select class="form-control form-control-sm fallback-select" v-model="nextOf(step).default">
              <option value="">مرحله بعدی در لیست</option>
              <option value="end">پایان فرم</option>
              <option v-for="item in otherSteps(step)" :key="`d-${step.id}-${item.id}`" :value="item.id">
                {{ item.title || item.id }}
              </option>
            </select>
          </div>

          <button type="button" class="btn btn-sm btn-primary mt-3" @click="addRule(step)">
            + مسیر جدید از این مرحله
          </button>
        </div>

        <div v-else class="step-card-preview">
          <div v-for="(rule, ruleIndex) in rulesOf(step)" :key="`p-${rule.id || ruleIndex}`" class="preview-line">
            {{ describeRule(rule) }}
            <span class="chip-arrow">←</span>
            {{ stepLabel(rule.next) || '؟' }}
          </div>
          <div class="preview-line preview-fallback">
            در غیر این صورت
            <span class="chip-arrow">←</span>
            {{ stepLabel(nextOf(step).default) || 'مرحله بعدی در لیست' }}
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import FormPathRuleEditor from "./FormPathRuleEditor.vue";
import {parentStepOf, questionRefLabel, stepTitleOf} from "../formQuestionContext";

export default {
  name: "FormStepPaths",
  components: {FormPathRuleEditor},
  props: {
    questions: {
      type: Array,
      default: () => []
    },
    focusStepId: {
      default: null
    },
    onlyStepId: {
      default: null
    }
  },
  data() {
    return {
      activeStepId: null,
      editingKey: null
    }
  },
  computed: {
    visibleQuestions() {
      return (this.questions || []).filter(question => question.visibility !== false)
    },
    allSteps() {
      return this.visibleQuestions.filter(question => question.type === 'step')
    },
    steps() {
      if (this.onlyStepId === null || this.onlyStepId === '') {
        return this.allSteps
      }
      return this.allSteps.filter(step => String(step.id) === String(this.onlyStepId))
    }
  },
  watch: {
    steps: {
      immediate: true,
      handler(steps) {
        steps.forEach(step => this.ensureNextConfig(step))
        if (!steps.length) {
          this.activeStepId = null
          return
        }
        if (!steps.some(step => String(step.id) === String(this.activeStepId))) {
          this.activeStepId = steps[0].id
        }
      }
    },
    focusStepId(id) {
      if (id !== null && typeof id !== 'undefined' && id !== '') {
        this.activeStepId = id
        this.editingKey = null
      }
    }
  },
  methods: {
    ensureNextConfig(step) {
      if (!step.config) {
        this.$set(step, 'config', {})
      }
      if (!step.config.next) {
        this.$set(step.config, 'next', {default: '', rules: []})
      }
      if (typeof step.config.next.default === 'undefined') {
        this.$set(step.config.next, 'default', '')
      }
      if (!Array.isArray(step.config.next.rules)) {
        this.$set(step.config.next, 'rules', [])
      }
    },
    nextOf(step) {
      this.ensureNextConfig(step)
      return step.config.next
    },
    rulesOf(step) {
      return this.nextOf(step).rules
    },
    ruleCount(step) {
      return this.rulesOf(step).length
    },
    isActive(step) {
      if (this.onlyStepId) {
        return true
      }
      return String(this.activeStepId) === String(step.id)
    },
    selectStep(step) {
      if (String(this.activeStepId) !== String(step.id)) {
        this.activeStepId = step.id
        this.editingKey = null
      }
      this.$emit('select-step', step)
    },
    ruleKey(step, ruleIndex) {
      return `${step.id}:${ruleIndex}`
    },
    otherSteps(step) {
      return this.allSteps.filter(item => String(item.id) !== String(step.id))
    },
    stepById(id) {
      return this.allSteps.find(step => String(step.id) === String(id))
    },
    questionById(id) {
      return this.visibleQuestions.find(question => String(question.id) === String(id))
    },
    stepLabel(id) {
      if (id === '' || id === null || typeof id === 'undefined') {
        return ''
      }
      if (id === 'end') {
        return 'پایان فرم'
      }
      const step = this.stepById(id)
      return step ? (step.title || step.id) : String(id)
    },
    questionsInStep(step) {
      const start = this.visibleQuestions.findIndex(item => String(item.id) === String(step.id))
      if (start < 0) {
        return []
      }
      const items = []
      for (let index = start + 1; index < this.visibleQuestions.length; index++) {
        const item = this.visibleQuestions[index]
        if (item.type === 'step') {
          break
        }
        items.push(item)
      }
      return items
    },
    questionsOutsideStep(step) {
      const insideIds = this.questionsInStep(step).map(item => String(item.id))
      return this.visibleQuestions.filter(item => {
        return item.type !== 'step'
          && String(item.id) !== String(step.id)
          && insideIds.indexOf(String(item.id)) === -1
      })
    },
    addRule(step) {
      const rules = this.rulesOf(step)
      rules.push({
        id: `rule_${Date.now()}`,
        next: '',
        when: {all: [{question_id: '', step_id: '', variable: '', op: 'eq', value: ''}]}
      })
      this.editingKey = this.ruleKey(step, rules.length - 1)
    },
    removeRule(step, index) {
      this.rulesOf(step).splice(index, 1)
      this.editingKey = null
    },
    describeRule(rule) {
      const when = rule.when || {}
      const mode = when.any ? 'any' : 'all'
      const conditions = when.any || when.all || []
      const joiner = mode === 'any' ? ' یا ' : ' و '
      const parts = conditions.map(condition => this.describeCondition(condition)).filter(Boolean)
      return parts.length ? `اگر ${parts.join(joiner)}` : 'اگر شرط را کامل کن'
    },
    describeCondition(condition) {
      const question = this.questionById(condition.question_id)
      const questionName = question ? questionRefLabel(this.visibleQuestions, question) : ''
      if (!questionName && (condition.value === '' || typeof condition.value === 'undefined')) {
        return ''
      }
      const name = questionName || this.fallbackConditionName(condition)
      if (condition.op === 'between' && Array.isArray(condition.value)) {
        return `${name} بین ${condition.value[0]} و ${condition.value[1]}`
      }
      if (condition.op === 'in' && Array.isArray(condition.value)) {
        return `${name} یکی از ${condition.value.join('، ')}`
      }
      const labels = {
        eq: 'برابر',
        neq: 'نابرابر',
        gt: 'بزرگتر از',
        gte: 'حداقل',
        lt: 'کوچکتر از',
        lte: 'حداکثر'
      }
      const valueLabel = this.optionLabel(condition.question_id, condition.value)
      return `${name} ${labels[condition.op] || condition.op} ${valueLabel}`
    },
    fallbackConditionName(condition) {
      const variable = condition.variable || 'سؤال'
      const step = condition.step_id ? this.stepById(condition.step_id) : parentStepOf(this.visibleQuestions, condition.question_id)
      return `${variable} · ${stepTitleOf(step)}`
    },
    optionLabel(questionId, value) {
      const question = this.questionById(questionId)
      if (question && Array.isArray(question.options)) {
        const option = question.options.find(item => String(item.id) === String(value))
        if (option) {
          return option.label || value
        }
      }
      return value
    }
  }
}
</script>

<style scoped>
.form-path-board {
  background: #f4f1fa;
  border: 1px solid #e0d7f3;
  border-radius: 12px;
  padding: 10px;
  margin-bottom: 8px;
}

.board-head {
  margin-bottom: 12px;
}

.empty-board {
  background: #fff;
  border-radius: 10px;
  padding: 16px;
  color: #6c757d;
}

.board-steps {
  display: grid;
  gap: 10px;
}

.step-card {
  background: #fff;
  border: 1px solid #ece6f7;
  border-radius: 12px;
  padding: 12px;
  cursor: pointer;
}

.step-card.active {
  border-color: #6f42c1;
  box-shadow: 0 0 0 3px rgba(111, 66, 193, 0.12);
}

.step-card-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.step-index {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  margin-left: 8px;
  border-radius: 50%;
  background: #6f42c1;
  color: #fff;
  font-size: 12px;
}

.step-hint {
  font-size: 12px;
  color: #6f42c1;
}

.step-card-body,
.step-card-preview {
  margin-top: 12px;
}

.path-chip,
.preview-line,
.fallback-row {
  width: 100%;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  text-align: right;
  background: #f6f3fb;
  border: 0;
  border-right: 3px solid #6f42c1;
  border-radius: 8px;
  padding: 8px 10px;
  margin-bottom: 6px;
  font-size: 13px;
}

.preview-fallback,
.fallback-row {
  background: #f3f4f6;
  border-right-color: #6c757d;
}

.fallback-row {
  flex-wrap: nowrap;
}

.fallback-row > span:first-child {
  white-space: nowrap;
  flex: 0 0 auto;
}

.chip-arrow {
  color: #6c757d;
  flex: 0 0 auto;
}

.fallback-select {
  flex: 1 1 auto;
  min-width: 0;
  max-width: none;
  height: 36px;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 4px 12px;
  padding-inline-end: 28px;
  line-height: 1.6;
  color: #374151;
}
</style>
